import { put, takeLatest, call } from 'redux-saga/effects'
import fetchFromAPI, { getDefaultAutorizedHeaders } from 'helpers/fetchFromApi'
import * as ApiRoute from 'constants/apiRouting'
import NoticeHelper from 'helpers/notice'
import UrlHelper from 'helpers/url'
import {
  JOB_WORKFLOW_FETCH_TABLE_DATA,
  JOB_WORKFLOW_FETCH_FORM_DATA,
  JOB_WORKFLOW_CHANGE,
  JOB_WORKFLOW_DELETE,
  JOB_WORKFLOW_POSITION_UPDATE_FETCH,
  JOB_WORKFLOW_POSITION_UPDATE_SUCCESS,
  JOB_WORKFLOW_FETCH_TABLE_DATA_SUCCESS,
  JOB_WORKFLOW_FETCH_TABLE_DATA_FAILURE,
  JOB_WORKFLOW_FETCH_FORM_DATA_SUCCESS,
  JOB_WORKFLOW_FETCH_FORM_DATA_FAILURE,
  LOAD_JOB_WORKFLOW_FORM,
  JOB_WORKFLOW_COPY_DATA,
  JOB_WORKFLOW_TRIGGERED_BY,
} from './reducer'
import { SHOW_NOTICE } from '../notice'

function* jobWorkflowFetchTableData(action) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: `${ApiRoute.API_URL_JOB_WORKFLOW_TABLE}${UrlHelper.getPsfsQuery(
        action.payload,
      )}`,
    })

    if (!response || !response.error) {
      yield put({
        type: JOB_WORKFLOW_FETCH_TABLE_DATA_SUCCESS,
        payload: response,
      })
    } else {
      yield put({
        type: JOB_WORKFLOW_FETCH_TABLE_DATA_FAILURE,
        error: response.errors,
      })
    }
  } catch (error) {
    console.log('JOB WORKFLOW INTERFACE ERROR', error)
  }
}

function* jobWorkflowFetchFormData(action) {
  try {
    const { entityId } = action.payload
    yield put({ type: LOAD_JOB_WORKFLOW_FORM, payload: true })

    const formData = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.API_URL_JOB_WORKFLOW_ITEM, {
        id: entityId,
      }),
    })

    if (formData.hasOwnProperty('error')) {
      yield put({ type: JOB_WORKFLOW_FETCH_FORM_DATA_FAILURE })
    } else {
      yield put({
        type: JOB_WORKFLOW_FETCH_FORM_DATA_SUCCESS,
        payload: formData,
      })
    }

    yield put({ type: LOAD_JOB_WORKFLOW_FORM, payload: false })
  } catch (error) {
    console.log('JOB WORKFLOWS ERROR', error)
  }
}

function* jobWorkflowChange(action) {
  try {
    const { entityId, data } = action.payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: !entityId ? 'POST' : 'PUT',
      partUrl: !entityId
        ? ApiRoute.API_URL_JOB_WORKFLOW_LIST
        : `${ApiRoute.API_URL_JOB_WORKFLOW_LIST}/${entityId}`,
      data,
    })

    if (response.error) {
      return yield call(action.promise.resolve, response.errors)
    }

    yield put({
      type: SHOW_NOTICE,
      notice: NoticeHelper.createSuccessObj(
        `Job workflow ${!entityId ? 'create' : 'change'} successful`,
      ),
    })

    return yield call(action.promise.resolve, false)
  } catch (error) {
    throw new Error(`JOB WORKFLOW INTERFACE ERROR: ${error}`)
  }
}

function* jobWorkflowTriggeredBy({ payload: { entityId, sites }, promise }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      partUrl: UrlHelper.create(ApiRoute.API_URL_JOB_WORKFLOW_TRIGGERED_BY, {
        id: entityId,
        sites,
      }),
    })

    if (response && !response.error) {
      return yield call(promise.resolve, response)
    }
  } catch (error) {
    throw new Error(`JOB WORKFLOW INTERFACE ERROR: ${error}`)
  }
}

function* jobWorkflowDelete(action) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'DELETE',
      partUrl: `${ApiRoute.API_URL_JOB_WORKFLOW_LIST}/${action.payload.entityId}`,
    })

    if (response === 204) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj('Job Workflow delete successful'),
      })

      return yield jobWorkflowFetchTableData(action)
    }

    console.log('JOB WORKFLOW DELETE UNKNOWN ERROR. RESPONSE: ', response)
  } catch (error) {
    console.log('JOB WORKFLOW INTERFACE ERROR', error)
  }
}

function* jobWorkflowPositionUpdate({ payload: { params, data, index } }) {
  try {
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PATCH',
      partUrl: UrlHelper.create(ApiRoute.JOB_WORKFLOW_POSITION_CHANGE, params),
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(
          'Job Workflow position successfully changed',
        ),
      })
      yield put({
        type: JOB_WORKFLOW_POSITION_UPDATE_SUCCESS,
        payload: {
          index,
          position: data.position,
        },
      })
    } else {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createWarningObj('Sorry, something went wrong'),
      })
    }
  } catch (error) {
    console.log('JOB WORKFLOW POSITION UPDATE ERROR', error)
  }
}

function* jobWorkflowCopyData(action) {
  try {
    const { entityId, data } = action.payload
    const response = yield call(fetchFromAPI, {
      headers: getDefaultAutorizedHeaders(),
      method: 'PUT',
      partUrl: UrlHelper.create(ApiRoute.API_URL_JOB_WORKFLOW_COPY_DATA, {
        id: entityId,
      }),
      data,
    })

    if (response && !response.error) {
      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createSuccessObj(
          'Job Workflow copy data successful',
        ),
      })
    }

    if (response.errors) {
      const message = Object.values(response.errors).reduce((acc, error) => {
        // eslint-disable-next-line no-param-reassign
        acc += `${error} `

        return acc
      }, '')

      yield put({
        type: SHOW_NOTICE,
        notice: NoticeHelper.createErrorObj(message),
      })
    }
  } catch (error) {
    console.log('JOB WORKFLOW INTERFACE ERROR', error)
  }
}

function* jobWorkflowSaga() {
  yield takeLatest(JOB_WORKFLOW_FETCH_TABLE_DATA, jobWorkflowFetchTableData)
  yield takeLatest(JOB_WORKFLOW_FETCH_FORM_DATA, jobWorkflowFetchFormData)
  yield takeLatest(JOB_WORKFLOW_CHANGE, jobWorkflowChange)
  yield takeLatest(JOB_WORKFLOW_DELETE, jobWorkflowDelete)
  yield takeLatest(
    JOB_WORKFLOW_POSITION_UPDATE_FETCH,
    jobWorkflowPositionUpdate,
  )
  yield takeLatest(JOB_WORKFLOW_COPY_DATA, jobWorkflowCopyData)
  yield takeLatest(JOB_WORKFLOW_TRIGGERED_BY, jobWorkflowTriggeredBy)
}

export default jobWorkflowSaga
